'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import AppLogo from '@/components/ui/AppLogo';

const NHL_TEAMS = [
  { id: 'TOR', name: 'Toronto Maple Leafs', conf: 'East', div: 'Atlantic' },
  { id: 'MTL', name: 'Montréal Canadiens', conf: 'East', div: 'Atlantic' },
  { id: 'BOS', name: 'Boston Bruins', conf: 'East', div: 'Atlantic' },
  { id: 'TBL', name: 'Tampa Bay Lightning', conf: 'East', div: 'Atlantic' },
  { id: 'FLA', name: 'Florida Panthers', conf: 'East', div: 'Atlantic' },
  { id: 'BUF', name: 'Buffalo Sabres', conf: 'East', div: 'Atlantic' },
  { id: 'DET', name: 'Detroit Red Wings', conf: 'East', div: 'Atlantic' },
  { id: 'OTT', name: 'Ottawa Senators', conf: 'East', div: 'Atlantic' },
  { id: 'NYR', name: 'New York Rangers', conf: 'East', div: 'Metro' },
  { id: 'NYI', name: 'New York Islanders', conf: 'East', div: 'Metro' },
  { id: 'NJD', name: 'New Jersey Devils', conf: 'East', div: 'Metro' },
  { id: 'PHI', name: 'Philadelphia Flyers', conf: 'East', div: 'Metro' },
  { id: 'PIT', name: 'Pittsburgh Penguins', conf: 'East', div: 'Metro' },
  { id: 'WSH', name: 'Washington Capitals', conf: 'East', div: 'Metro' },
  { id: 'CAR', name: 'Carolina Hurricanes', conf: 'East', div: 'Metro' },
  { id: 'CBJ', name: 'Columbus Blue Jackets', conf: 'East', div: 'Metro' },
  { id: 'COL', name: 'Colorado Avalanche', conf: 'West', div: 'Central' },
  { id: 'DAL', name: 'Dallas Stars', conf: 'West', div: 'Central' },
  { id: 'MIN', name: 'Minnesota Wild', conf: 'West', div: 'Central' },
  { id: 'NSH', name: 'Nashville Predators', conf: 'West', div: 'Central' },
  { id: 'STL', name: 'St. Louis Blues', conf: 'West', div: 'Central' },
  { id: 'WPG', name: 'Winnipeg Jets', conf: 'West', div: 'Central' },
  { id: 'CHI', name: 'Chicago Blackhawks', conf: 'West', div: 'Central' },
  { id: 'ARI', name: 'Arizona Coyotes', conf: 'West', div: 'Central' },
  { id: 'VGK', name: 'Vegas Golden Knights', conf: 'West', div: 'Pacific' },
  { id: 'EDM', name: 'Edmonton Oilers', conf: 'West', div: 'Pacific' },
  { id: 'CGY', name: 'Calgary Flames', conf: 'West', div: 'Pacific' },
  { id: 'VAN', name: 'Vancouver Canucks', conf: 'West', div: 'Pacific' },
  { id: 'SEA', name: 'Seattle Kraken', conf: 'West', div: 'Pacific' },
  { id: 'LAK', name: 'Los Angeles Kings', conf: 'West', div: 'Pacific' },
  { id: 'ANA', name: 'Anaheim Ducks', conf: 'West', div: 'Pacific' },
  { id: 'SJS', name: 'San Jose Sharks', conf: 'West', div: 'Pacific' },
];

const ALERT_TYPES = [
  { id: 'injuries', label: 'Blessures & Retours', icon: 'HeartIcon', desc: 'Alertes immédiates sur blessures joueurs clés' },
  { id: 'lineup', label: 'Changements Alignement', icon: 'UsersIcon', desc: 'Confirmations formations avant match' },
  { id: 'goalie', label: 'Confirmations Gardiens', icon: 'ShieldCheckIcon', desc: 'Gardiens partants confirmés' },
  { id: 'odds', label: 'Mouvements de Cotes', icon: 'ArrowTrendingUpIcon', desc: 'Anomalies et mouvements sharp' },
  { id: 'projections', label: 'Projections ML', icon: 'SparklesIcon', desc: 'Nouvelles projections haute confiance' },
  { id: 'live', label: 'Événements Live', icon: 'BoltIcon', desc: 'Buts, pénalités, momentum en direct' },
];

const STEPS = ['Bienvenue', 'Équipes Favorites', 'Préférences Alertes', 'Expérience', 'Prêt !'];

export default function OnboardingClient() {
  const router = useRouter();
  const [step, setStep] = useState(0);
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const [selectedAlerts, setSelectedAlerts] = useState<string[]>(['injuries', 'goalie', 'projections']);
  const [experience, setExperience] = useState<'beginner' | 'intermediate' | 'expert'>('intermediate');
  const [completing, setCompleting] = useState(false);

  const toggleTeam = (id: string) => {
    setSelectedTeams((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : prev.length < 5 ? [...prev, id] : prev
    );
  };

  const toggleAlert = (id: string) => {
    setSelectedAlerts((prev) =>
      prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]
    );
  };

  const handleComplete = async () => {
    setCompleting(true);
    await new Promise((r) => setTimeout(r, 1200));
    router.push('/');
  };

  const progress = ((step + 1) / STEPS.length) * 100;

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
      {/* Progress bar */}
      <div className="w-full max-w-2xl mb-8">
        <div className="flex items-center justify-between mb-3">
          {STEPS.map((s, i) => (
            <div key={s} className="flex flex-col items-center gap-1">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                  i < step ? 'bg-positive text-white' : i === step ? 'bg-primary text-white ring-2 ring-primary/30' : 'bg-card border border-border text-muted-foreground'
                }`}
              >
                {i < step ? '✓' : i + 1}
              </div>
              <span className={`text-2xs hidden sm:block ${i === step ? 'text-primary font-medium' : 'text-muted-foreground'}`}>{s}</span>
            </div>
          ))}
        </div>
        <div className="h-1.5 bg-card rounded-full overflow-hidden">
          <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>
      </div>

      {/* Card */}
      <div className="w-full max-w-2xl bg-card border border-border rounded-2xl p-8 shadow-2xl">
        {/* Step 0 — Welcome */}
        {step === 0 && (
          <div className="text-center space-y-6">
            <div className="flex justify-center mb-4">
              <AppLogo size={56} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground mb-2">Bienvenue sur NHL Analytics Pro</h1>
              <p className="text-muted-foreground">La plateforme d'intelligence sportive de nouvelle génération. Configurons votre expérience en 2 minutes.</p>
            </div>

            {/* Free Trial Banner */}
            <div className="flex items-center gap-3 p-4 rounded-xl bg-positive/10 border border-positive/30 text-left">
              <div className="w-10 h-10 rounded-full bg-positive/20 flex items-center justify-center flex-shrink-0">
                <Icon name="GiftIcon" size={20} className="text-positive" />
              </div>
              <div>
                <div className="text-sm font-semibold text-foreground">🎉 1 mois gratuit — Offre de bienvenue</div>
                <div className="text-xs text-muted-foreground">Accès complet à toutes les fonctionnalités pendant 30 jours. Puis $29/mois. Annulation à tout moment.</div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 py-4">
              {[
                { icon: 'SparklesIcon', label: 'IA Prédictive', desc: 'Modèles ML avancés' },
                { icon: 'BoltIcon', label: 'Données Live', desc: 'NHL en temps réel' },
                { icon: 'ChartBarIcon', label: 'Analytics Pro', desc: 'Insights institutionnels' },
              ].map((f) => (
                <div key={f.label} className="p-4 rounded-xl bg-background border border-border text-center">
                  <Icon name={f.icon as any} size={24} className="text-primary mx-auto mb-2" />
                  <div className="text-xs font-semibold text-foreground">{f.label}</div>
                  <div className="text-2xs text-muted-foreground mt-0.5">{f.desc}</div>
                </div>
              ))}
            </div>
            <button onClick={() => setStep(1)} className="w-full py-3 bg-primary text-white rounded-xl font-semibold hover:bg-primary/90 transition-colors">
              Commencer — 1 mois gratuit →
            </button>
            <p className="text-xs text-muted-foreground">Aucune carte de crédit requise · Puis $29/mois après 30 jours</p>
          </div>
        )}

        {/* Step 1 — Team Selection */}
        {step === 1 && (
          <div className="space-y-5">
            <div>
              <h2 className="text-xl font-bold text-foreground">Vos équipes favorites</h2>
              <p className="text-sm text-muted-foreground mt-1">Sélectionnez jusqu'à 5 équipes pour personnaliser votre dashboard. ({selectedTeams.length}/5)</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 max-h-72 overflow-y-auto pr-1">
              {NHL_TEAMS.map((team) => {
                const selected = selectedTeams.includes(team.id);
                return (
                  <button
                    key={team.id}
                    onClick={() => toggleTeam(team.id)}
                    className={`p-2.5 rounded-lg border text-left transition-all text-xs ${
                      selected
                        ? 'border-primary bg-primary/10 text-primary' :'border-border bg-background text-muted-foreground hover:border-primary/40 hover:text-foreground'
                    }`}
                  >
                    <div className="font-bold">{team.id}</div>
                    <div className="text-2xs opacity-70 truncate">{team.name.split(' ').slice(-1)[0]}</div>
                  </button>
                );
              })}
            </div>
            <div className="flex gap-3">
              <button onClick={() => setStep(0)} className="flex-1 py-2.5 border border-border rounded-xl text-sm text-muted-foreground hover:text-foreground transition-colors">← Retour</button>
              <button onClick={() => setStep(2)} className="flex-2 px-8 py-2.5 bg-primary text-white rounded-xl text-sm font-semibold hover:bg-primary/90 transition-colors">
                Continuer →
              </button>
            </div>
          </div>
        )}

        {/* Step 2 — Alert Preferences */}
        {step === 2 && (
          <div className="space-y-5">
            <div>
              <h2 className="text-xl font-bold text-foreground">Préférences d'alertes</h2>
              <p className="text-sm text-muted-foreground mt-1">Choisissez les types d'alertes que vous souhaitez recevoir.</p>
            </div>
            <div className="space-y-2">
              {ALERT_TYPES.map((alert) => {
                const active = selectedAlerts.includes(alert.id);
                return (
                  <button
                    key={alert.id}
                    onClick={() => toggleAlert(alert.id)}
                    className={`w-full flex items-center gap-4 p-4 rounded-xl border transition-all ${
                      active ? 'border-primary bg-primary/5' : 'border-border bg-background hover:border-primary/30'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${active ? 'bg-primary/20' : 'bg-card'}`}>
                      <Icon name={alert.icon as any} size={20} className={active ? 'text-primary' : 'text-muted-foreground'} />
                    </div>
                    <div className="text-left flex-1">
                      <div className={`text-sm font-medium ${active ? 'text-foreground' : 'text-muted-foreground'}`}>{alert.label}</div>
                      <div className="text-xs text-muted-foreground">{alert.desc}</div>
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${active ? 'border-primary bg-primary' : 'border-border'}`}>
                      {active && <div className="w-2 h-2 rounded-full bg-white" />}
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="flex gap-3">
              <button onClick={() => setStep(1)} className="flex-1 py-2.5 border border-border rounded-xl text-sm text-muted-foreground hover:text-foreground transition-colors">← Retour</button>
              <button onClick={() => setStep(3)} className="flex-2 px-8 py-2.5 bg-primary text-white rounded-xl text-sm font-semibold hover:bg-primary/90 transition-colors">
                Continuer →
              </button>
            </div>
          </div>
        )}

        {/* Step 3 — Experience Level */}
        {step === 3 && (
          <div className="space-y-5">
            <div>
              <h2 className="text-xl font-bold text-foreground">Votre niveau d'expérience</h2>
              <p className="text-sm text-muted-foreground mt-1">Nous adaptons l'interface à votre profil analytique.</p>
            </div>
            <div className="space-y-3">
              {[
                { id: 'beginner', label: 'Débutant', desc: 'Je découvre l\'analytics sportif. Interface simplifiée avec guides.', icon: 'AcademicCapIcon' },
                { id: 'intermediate', label: 'Intermédiaire', desc: 'Je connais les bases. Interface standard avec tooltips.', icon: 'ChartBarIcon' },
                { id: 'expert', label: 'Expert / Quant', desc: 'Analyste professionnel. Interface dense, terminal mode, données brutes.', icon: 'CommandLineIcon' },
              ].map((lvl) => (
                <button
                  key={lvl.id}
                  onClick={() => setExperience(lvl.id as any)}
                  className={`w-full flex items-center gap-4 p-5 rounded-xl border transition-all ${
                    experience === lvl.id ? 'border-primary bg-primary/5 ring-1 ring-primary/20' : 'border-border bg-background hover:border-primary/30'
                  }`}
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${experience === lvl.id ? 'bg-primary/20' : 'bg-card'}`}>
                    <Icon name={lvl.icon as any} size={24} className={experience === lvl.id ? 'text-primary' : 'text-muted-foreground'} />
                  </div>
                  <div className="text-left">
                    <div className={`font-semibold ${experience === lvl.id ? 'text-foreground' : 'text-muted-foreground'}`}>{lvl.label}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{lvl.desc}</div>
                  </div>
                </button>
              ))}
            </div>
            <div className="flex gap-3">
              <button onClick={() => setStep(2)} className="flex-1 py-2.5 border border-border rounded-xl text-sm text-muted-foreground hover:text-foreground transition-colors">← Retour</button>
              <button onClick={() => setStep(4)} className="flex-2 px-8 py-2.5 bg-primary text-white rounded-xl text-sm font-semibold hover:bg-primary/90 transition-colors">
                Continuer →
              </button>
            </div>
          </div>
        )}

        {/* Step 4 — Ready */}
        {step === 4 && (
          <div className="text-center space-y-6">
            <div className="w-20 h-20 rounded-full bg-positive/10 flex items-center justify-center mx-auto">
              <Icon name="CheckCircleIcon" size={40} className="text-positive" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-foreground mb-2">Vous êtes prêt !</h2>
              <p className="text-muted-foreground">Votre profil est configuré. Accédez à votre dashboard personnalisé.</p>
            </div>

            {/* Trial Confirmation */}
            <div className="flex items-center gap-3 p-4 rounded-xl bg-positive/10 border border-positive/30 text-left">
              <div className="w-10 h-10 rounded-full bg-positive/20 flex items-center justify-center flex-shrink-0">
                <Icon name="GiftIcon" size={20} className="text-positive" />
              </div>
              <div>
                <div className="text-sm font-semibold text-foreground">Votre essai gratuit de 30 jours commence maintenant</div>
                <div className="text-xs text-muted-foreground">Accès complet jusqu'au terme de votre période d'essai. Puis $29/mois.</div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 py-2">
              <div className="p-3 rounded-xl bg-background border border-border">
                <div className="text-lg font-bold text-primary">{selectedTeams.length}</div>
                <div className="text-2xs text-muted-foreground">Équipes</div>
              </div>
              <div className="p-3 rounded-xl bg-background border border-border">
                <div className="text-lg font-bold text-primary">{selectedAlerts.length}</div>
                <div className="text-2xs text-muted-foreground">Alertes</div>
              </div>
              <div className="p-3 rounded-xl bg-background border border-border">
                <div className="text-lg font-bold text-primary capitalize">{experience === 'beginner' ? 'Déb.' : experience === 'intermediate' ? 'Inter.' : 'Expert'}</div>
                <div className="text-2xs text-muted-foreground">Niveau</div>
              </div>
            </div>
            <button
              onClick={handleComplete}
              disabled={completing}
              className="w-full py-3.5 bg-primary text-white rounded-xl font-semibold hover:bg-primary/90 transition-colors disabled:opacity-70 flex items-center justify-center gap-2"
            >
              {completing ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Configuration en cours...
                </>
              ) : (
                'Accéder au Dashboard →'
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
